import React, { useCallback, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStickyFooter from '../../OfferPageStickyFooter'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import { DAY_FORMAT, DAY_MONTH_NAME_SHORT, DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import useIsAvailableForPurchase from 'components/OfferPage/hooks/useIsAvailableForPurchase'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import FlashHotelAvailabilityButton from './FlashHotelAvailabilityButton'
import { pluralizeToString } from 'lib/string/pluralize'
import MultiRoomOfferBookButton from 'components/OfferPage/MultipleRoomsBooking/MultiRoomOfferBookButton'
import { hasDifferentPackagesForRooms } from 'selectors/offerPage/multipleRoomBookingSelectors'
import styled from 'styled-components'
import { rem } from 'polished'
import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import moment from 'moment'
import MultiRoomOfferAddToCartButton from 'components/OfferPage/MultipleRoomsBooking/MultiRoomOfferAddToCartButton'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'

const StyledOfferPageStickyFooter = styled(OfferPageStickyFooter)`
  padding: ${rem(12)} ${rem(20)} ${rem(20)} ${rem(20)};
`
interface Props {
  offer: App.Offer;
  rooms?: Array<App.RoomOccupants>;
  checkInDate?: string;
  duration: number;
  hasMultiRoomDifferentPackages?: boolean;
  userMultiItemCartEnabled: boolean;
}

function HotelOfferPageStickyFooterVariant(props: Props) {
  const {
    rooms = EmptyArray,
    checkInDate,
    duration,
    offer,
    hasMultiRoomDifferentPackages,
    userMultiItemCartEnabled,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const availableForPurchase = useIsAvailableForPurchase()

  const guestsLabel = useMemo(() => `${formatOccupantsAsGuests(...rooms)} (${pluralizeToString(offer.saleUnit, rooms.length)})`, [offer.saleUnit, rooms])
  const dateText = useMemo(() => {
    if (checkInDate) {
      const checkIn = moment(checkInDate)
      const checkOut = checkIn.clone().add(duration, 'days')
      if (checkIn.isSame(checkOut, 'month')) {
        return `${checkIn.format(DAY_FORMAT)} - ${checkOut.format(DAY_MONTH_NAME_SHORT)}`
      } else if (checkIn.isSame(checkOut, 'year')) {
        return `${checkIn.format(DAY_MONTH_NAME_SHORT)} - ${checkOut.format(DAY_MONTH_NAME_SHORT)}`
      } else {
        return `${checkIn.format(DMY_CASUAL_SHORT_FORMAT)} - ${checkOut.format(DMY_CASUAL_SHORT_FORMAT)}`
      }
    }
  }, [checkInDate, duration])

  const onOpenCalendar = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const canBook = !!(checkInDate && rooms && availableForPurchase)

  const onOpenCapcity = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const canBookNowMultiRoom = rooms.length > 1 && canBook && hasMultiRoomDifferentPackages

  return (
    <StyledOfferPageStickyFooter
      visible
      callToActionDisabled
      offer={offer}
    >
      <Group direction="vertical" gap={12} fullWidth>
        <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
          {!!checkInDate && <FilterChip
            endIcon={<LineEditAltIcon />}
            selected
            size="medium"
            onClick={onOpenCalendar}
          >
            {dateText}
          </FilterChip>}
          {rooms.length > 0 && <FilterChip
            endIcon={<LineEditAltIcon />}
            selected
            size="medium"
            onClick={onOpenCapcity}
          >
            {guestsLabel}
          </FilterChip>}
        </Group>
        <SoftOfferPageWalledGarden>
          {canBookNowMultiRoom && <Group direction="horizontal" gap={8}>
            {userMultiItemCartEnabled && <MultiRoomOfferAddToCartButton/>}
            <MultiRoomOfferBookButton fit="full-width" />
          </Group>}
          {!canBookNowMultiRoom && <FlashHotelAvailabilityButton fit="full-width"/>}
        </SoftOfferPageWalledGarden>
      </Group>
    </StyledOfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  hasMultiRoomDifferentPackages: hasDifferentPackagesForRooms(state, appState),
  offer: state.offer,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  duration: state.duration,
  userMultiItemCartEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.croMultiItemCart),
}))(HotelOfferPageStickyFooterVariant)
